import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { AuthProvider } from './lib/auth.js';
import { loadConfig } from './lib/api.js';
import './styles.css';

/**
 * The app cannot render until it knows how to authenticate, so the config
 * endpoint is fetched before mounting. Doing it here rather than inside a
 * component avoids a flash of the signed-out screen for an already-signed-in
 * user.
 */
async function start(): Promise<void> {
  const root = document.getElementById('root');
  if (!root) throw new Error('No #root element');

  try {
    const config = await loadConfig();
    createRoot(root).render(
      <StrictMode>
        <BrowserRouter>
          <AuthProvider config={config}>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </StrictMode>,
    );
  } catch (err) {
    // A failure here means the API is unreachable, which no amount of React
    // will fix -- say so plainly instead of rendering an empty shell.
    root.innerHTML = `
      <div style="font-family:system-ui;max-width:38rem;margin:15vh auto;padding:0 1.5rem">
        <h1 style="font-size:1.1rem">Cannot reach the signature manager API</h1>
        <p style="color:#666;font-size:.9rem">${(err as Error).message}</p>
        <p style="color:#666;font-size:.9rem">
          If you are running locally, start the API with <code>npm run dev:api</code>.
        </p>
      </div>`;
  }
}

void start();
